import logger from "./logger";

interface ContactLinksGeneratorProps {
  number: string;
  email?: string;
  message?: string;
}

export default class ContactLinksGenerator {
  public _number: string; // Changed from private to public
  private _email: string;
  private _message: string;

  constructor({ number, email = "", message = "" }: ContactLinksGeneratorProps) {
    logger.log("[ContactLinksGenerator] Constructor input number:", number);
    
    // Format the phone number with the default country code if needed
    this._number = this.formatPhoneNumber(number, "+91"); // Default to +91 for India
    logger.log("[ContactLinksGenerator] Formatted number result:", this._number);
    
    this._email = email;
    this._message = message;
  }

 private formatPhoneNumber(phoneNumber: string, defaultCountryCode: string = "+91"): string {
  logger.log("[formatPhoneNumber] Smart processing input:", phoneNumber);
  
  // Step 1: Clean the number - remove everything except digits and +
  let cleaned = phoneNumber?.toString()?.replace(/[^\d+]/g, '') || '';
  logger.log("[formatPhoneNumber] After cleaning:", cleaned);
  
  // Step 2: Handle already formatted numbers
  if (cleaned.startsWith('+')) {
    logger.log("[formatPhoneNumber] Already formatted with +");
    return cleaned;
  }
  
  // Step 3: Handle 00 international prefix
  if (cleaned.startsWith('00')) {
    logger.log("[formatPhoneNumber] Removing 00 international prefix");
    cleaned = cleaned.substring(2);
    return `+${cleaned}`;
  }
  
  // Step 4: Handle numbers starting with 91 (special Indian case)
  if (cleaned.startsWith('91') && cleaned.length === 12) {
    logger.log("[formatPhoneNumber] Indian number with 91 prefix (91 + 10 digits)");
    return `+${cleaned}`;
  }
  
  // Step 5: Length-based processing
  const length = cleaned.length;
  logger.log("[formatPhoneNumber] Processing length:", length);
  
  if (length === 9) {
    logger.log("[formatPhoneNumber] 9 digits - Adding +91 (assume incomplete Indian)");
    return `+91${cleaned}`;
  }
  
  if (length === 10) {
    const firstDigit = cleaned[0];
    logger.log("[formatPhoneNumber] 10 digits, first digit:", firstDigit);
    
    if (firstDigit === '0') {
      logger.log("[formatPhoneNumber] Starts with 0 - Removing and reprocessing");
      return this.formatPhoneNumber(cleaned.substring(1), defaultCountryCode);
    }
    
    if (['5', '6', '7', '8', '9'].includes(firstDigit)) {
      logger.log("[formatPhoneNumber] Indian mobile (starts with", firstDigit, ") - Adding +91");
      return `+91${cleaned}`;
    }
    
    if (['1', '2', '3', '4'].includes(firstDigit)) {
      logger.log("[formatPhoneNumber] International single digit country code", firstDigit);
      return `+${cleaned}`;
    }
  }
  
  // Step 6: 11+ digits - Smart country code detection
  if (length >= 11) {
    const result = this.detectCountryCode(cleaned);
    if (result) {
      logger.log("[formatPhoneNumber] Country code detected:", result);
      return `+${cleaned}`;
    }
  }
  
  // Fallback to default country code
  logger.log("[formatPhoneNumber] No country code detected - Using default +91");
  return `+91${cleaned}`;
}

// ADD THIS NEW HELPER METHOD:
private detectCountryCode(phoneNumber: string): boolean {
  const length = phoneNumber.length;
  
  // Common country codes by length
  const countryCodesByLength: { [key: number]: string[] } = {
    1: ['1', '7'],  // US/Canada, Russia
    2: ['91', '44', '86', '81', '33', '49', '39', '34', '46', '47', '31', '32', '41', '43', '45', '48', '90', '82', '84', '65', '60', '62', '63', '64', '66', '95', '94', '92', '93', '98', '20', '27', '30', '36', '40', '51', '52', '53', '54', '55', '56', '57', '58', '61'],
    3: ['852', '853', '886', '380', '381', '382', '383', '385', '386', '387', '389', '971', '972', '973', '974', '975', '976', '977', '992', '993', '994', '995', '996', '998']
  };
  
  const isValidCountryCode = (code: string, codeLength: number): boolean => {
    return countryCodesByLength[codeLength]?.includes(code) || false;
  };
  
  if (length === 11) {
    // Priority 1: 2-digit country code + 9 digits (more realistic)
    const cc2 = phoneNumber.substring(0, 2);
    if (isValidCountryCode(cc2, 2)) return true;
    
    // Priority 2: 1-digit country code + 10 digits (fallback)
    const cc1 = phoneNumber.substring(0, 1);
    if (isValidCountryCode(cc1, 1)) return true;
  }
  
  if (length === 12) {
    // Priority 1: 2-digit country code + 10 digits (most common)
    const cc2 = phoneNumber.substring(0, 2);
    if (isValidCountryCode(cc2, 2)) return true;
    
    // Priority 2: 3-digit country code + 9 digits
    const cc3 = phoneNumber.substring(0, 3);
    if (isValidCountryCode(cc3, 3)) return true;
    
    // Priority 3: 1-digit country code + 11 digits (rare)
    const cc1 = phoneNumber.substring(0, 1);
    if (isValidCountryCode(cc1, 1)) return true;
  }
  
  if (length === 13) {
    // Priority 1: 3-digit country code + 10 digits
    const cc3 = phoneNumber.substring(0, 3);
    if (isValidCountryCode(cc3, 3)) return true;
    
    // Priority 2: 2-digit country code + 11 digits
    const cc2 = phoneNumber.substring(0, 2);
    if (isValidCountryCode(cc2, 2)) return true;
    
    // Priority 3: 1-digit country code + 12 digits
    const cc1 = phoneNumber.substring(0, 1);
    if (isValidCountryCode(cc1, 1)) return true;
  }
  
  return false;
}

  get tel() {
    logger.log("[tel] Generated tel link:", `tel:${this._number}`);
    return `tel:${this._number}`;
  }

  get chat() {
    const result = `sms:${this._number}?body=${encodeURIComponent(this._message)}`;
    logger.log("[chat] Generated SMS link:", result);
    return result;
  }

  get mail() {
    logger.log("[mail] Generated mailto link:", `mailto:${this._email}`);
    return `mailto:${this._email}`;
  }

  get whatsapp() {
    logger.log("[whatsapp] Input number for WhatsApp:", this._number);
    
    // Use wa.me format and remove "+" sign from the phone number
    const whatsappNumber = this._number.replace(/\+/g, "");
    logger.log("[whatsapp] Number after removing + sign:", whatsappNumber);
    
    const messageQuery = this._message ? `?text=${encodeURIComponent(this._message)}` : "";
    const result = `https://wa.me/${whatsappNumber}${messageQuery}`;
    
    logger.log("[whatsapp] Final WhatsApp URL:", result);
    return result;
  }
}